import {
  ProductDetailsVariantFragment,
  RichTextFragment,
  SubscriptionProductDetailsFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { ICartState } from '@/ui/store/cart/types';
import { componentByTypename } from '@/ui/utils/componentByTypename';
import { DeliveryTimeRange } from '@/integrations/crystallize/types/localization/DeliveryTimeRange';
import { ParsedProductVariant } from '../components/pages/ProductDetail/types/ParsedProductVariant';
import { BaseProduct } from './BaseProductService';
import { isDigitalProductVariantInStock } from './utils/isVariantInStock';

export class Subscription<
  T extends SubscriptionProductDetailsFragment = SubscriptionProductDetailsFragment,
  V extends ParsedProductVariant = ParsedProductVariant,
> extends BaseProduct<T, V> {
  nukiIdHeading?: RichTextFragment;

  nukiIdSubtext?: RichTextFragment;

  constructor(data: T, cartStore: ICartState) {
    super(data, cartStore);

    this.nukiIdHeading =
      componentByTypename(data.nukiIdHeading?.content, 'RichTextContent') || undefined;
    this.nukiIdSubtext =
      componentByTypename(data.nukiIdSubtext?.content, 'RichTextContent') ?? undefined;
    this.variants = (data.variants?.map(this.parseProductVariant) as V[]) || [];
  }

  isSkuInStock(sku: string) {
    const productVariant = this.variants.find((variant) => variant.sku === sku);
    return isDigitalProductVariantInStock(productVariant);
  }

  isVariantInStock(productVariant: ParsedProductVariant) {
    return isDigitalProductVariantInStock(productVariant);
  }

  isLowStock(): boolean {
    return false;
  }

  get isAnyVariantInStock() {
    return this.variants.some((variant) => this.isVariantInStock(variant));
  }

  getStandardDeliveryTimeRange(): DeliveryTimeRange {
    return {
      min: 0,
      max: 0,
    };
  }

  getLowStockDeliveryTimeRange(): DeliveryTimeRange {
    return {
      min: 0,
      max: 0,
    };
  }

  getDeliveryTimeRange(): DeliveryTimeRange {
    return {
      min: 0,
      max: 0,
    };
  }

  addToCart(
    currentVariant: V,
    options?: {
      nukiIds?: string[];
      activationCode?: string;
      directPurchase?: boolean;
      quantity?: number;
    },
  ): void {
    if (options?.nukiIds && typeof options.nukiIds === 'object' && options.nukiIds.length > 0) {
      // Add product to cart for each nukiID inserted.
      // NOTE: when nukiIds are provided, `quantity` is ignored — one item is added per nukiId.
      options.nukiIds.forEach((nukiId) => {
        this.cartStore.addProduct(this, currentVariant, { nukiId, fixedQuantity: true });
      });
    } else {
      this.cartStore.addProduct(this, currentVariant, {
        ...options,
        fixedQuantity: true,
        quantity: options?.quantity,
      });
    }
    if (options?.directPurchase === true) {
      return;
    }
    this.cartStore.toggleCartDisplay();
  }

  protected parseProductVariant(rawVariant: ProductDetailsVariantFragment): ParsedProductVariant {
    const baseVariant = super.parseProductVariant(rawVariant);
    return {
      ...baseVariant,
      subscriptionPlan: rawVariant.subscriptionPlans?.[0],
    };
  }
}
